/**
 * Allys Project Detail Skeleton
 *
 * Matches AllysProjectDetailContent: hero carousel, 2-column overview grid,
 * navy unit-availability band, gallery grid, timeline, and CTA.
 * Includes navigation skeleton so the nav bar is always visible.
 */

import { AllysNavSkeleton } from './AllysNavSkeleton';

export function AllysProjectDetailSkeleton() {
  return (
    <div className="min-h-screen bg-white">
      <AllysNavSkeleton />

      {/* Hero carousel placeholder */}
      <section className="relative w-full aspect-21/9 max-h-[70vh] bg-gray-200 skeleton-shimmer skeleton-stagger-1">
        <div className="absolute inset-0 bg-linear-to-t from-black/40 to-transparent" />
        <div className="absolute bottom-8 left-8 space-y-3">
          <div className="h-10 w-80 rounded-lg bg-white/20 skeleton-shimmer" />
          <div className="h-5 w-48 rounded bg-white/15 skeleton-shimmer" />
        </div>
      </section>

      {/* Overview — 2-column grid */}
      <section className="py-20 lg:py-28 skeleton-stagger-2">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
            <div className="space-y-6">
              <div className="h-4 w-24 rounded-full bg-gray-200 skeleton-shimmer" />
              <div className="h-9 w-3/4 rounded-lg bg-gray-200 skeleton-shimmer" />
              <div className="space-y-2">
                <div className="h-4 w-full rounded bg-gray-100 skeleton-shimmer" />
                <div className="h-4 w-full rounded bg-gray-100 skeleton-shimmer" />
                <div className="h-4 w-5/6 rounded bg-gray-100 skeleton-shimmer" />
                <div className="h-4 w-2/3 rounded bg-gray-100 skeleton-shimmer" />
              </div>
              <div className="h-12 w-40 rounded-xl bg-[#243462]/15 mt-4 skeleton-shimmer" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-28 rounded-xl bg-gray-50 border border-gray-100 skeleton-shimmer" />
              ))}
              <div className="sm:col-span-2 h-28 rounded-xl bg-gray-50 border border-gray-100 skeleton-shimmer" />
            </div>
          </div>
        </div>
      </section>

      {/* Unit availability — navy band */}
      <section className="py-20 bg-[#243462] skeleton-stagger-3">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="h-4 w-28 rounded-full bg-white/10 mx-auto mb-4 skeleton-shimmer" />
          <div className="h-9 w-64 rounded-lg bg-white/15 mx-auto mb-10 skeleton-shimmer" />
          <div className="grid grid-cols-3 gap-6 max-w-lg mx-auto">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-2">
                <div className="h-10 w-16 rounded-lg bg-white/10 mx-auto skeleton-shimmer" />
                <div className="h-3 w-20 rounded bg-white/5 mx-auto skeleton-shimmer" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery grid */}
      <section className="py-20 lg:py-28 skeleton-stagger-4">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div className="h-8 w-32 rounded-lg bg-gray-200 skeleton-shimmer" />
            <div className="h-10 w-28 rounded-lg bg-gray-100 skeleton-shimmer" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="sm:col-span-2 sm:row-span-2 aspect-4/3 rounded-xl bg-gray-100 skeleton-shimmer" />
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="aspect-4/3 rounded-xl bg-gray-100 skeleton-shimmer" />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#040813] skeleton-stagger-5">
        <div className="max-w-3xl mx-auto px-6 text-center space-y-6">
          <div className="h-10 w-72 rounded-lg bg-white/10 mx-auto skeleton-shimmer" />
          <div className="h-5 w-96 max-w-full rounded bg-white/5 mx-auto skeleton-shimmer" />
          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-2">
            <div className="h-12 w-40 rounded-xl bg-[#BBA339]/20 skeleton-shimmer" />
            <div className="h-12 w-40 rounded-xl bg-white/10 skeleton-shimmer" />
          </div>
        </div>
      </section>
    </div>
  );
}
